/**
 * Short-code redemption — the pure half of the service worker's mount
 * credential path.
 *
 * A configured mount credential can be a chain short code (a CUID2 id the
 * server resolves to a stored chain leaf, `GET /auth/chains/id/:id`). The
 * leaf is cached on the credentials record beside the code (`leafToken`),
 * never over `token`, which is the stable handle across leaf renewals —
 * and stamped with the code that minted it (`leafCode`), so a leaf is
 * presentable only while its code is still the mount's code. An app that
 * hands the worker a new code (alert.live writes `{...existing,
 * ...server.auth}`, which keeps the old leaf beside the new code) must not
 * keep presenting the old grant until it lapses: a stale-but-valid leaf
 * provokes no 401, so nothing server-side would ever converge it
 * (a-changed-mount-short-code-must-retire-its-cached-leaf, 2026-10-30).
 * A retired code's leaf is discarded, not kept as a fallback, and a record
 * without the stamp is treated as unstamped: those are exactly the leaves
 * this exists to retire, at the cost of one redemption per server on
 * upgrade.
 *
 * Policy, per (server, code):
 *   - a cached leaf of the current code outside its final renew margin is
 *     used as-is;
 *   - otherwise one network redemption runs at a time — **concurrent
 *     callers join it** and receive its result (alert.live findings item
 *     20, 2026-09-09: before this, the first request stamped the throttle
 *     and the rest, with nothing cached, got null and presented a freshly
 *     minted user token the server had never seen — a genuine 401 the heal
 *     path could not fix);
 *   - after an attempt, no new network redemption for `minIntervalMs`
 *     (the endpoint is public and rate-limited) — an unexpired cached leaf
 *     is still presentable, revocation being enforced server-side at use.
 *
 * Every request loads its own credentials record, so a joiner's copy never
 * sees the winner's store write: the leaf is handed back directly and
 * stamped onto the joiner's record too, so the 401 heal path (which drops a
 * stale cached leaf) can recognise it.
 */

export const LEAF_CLOCK_SKEW_MS = 60 * 1000
// Re-redeem inside the leaf's final six hours — renewal mints a fresh leaf
// under the same short code, so early re-redemption picks up the rotated
// credential before the cached one lapses.
export const LEAF_RENEW_MARGIN_MS = 6 * 60 * 60 * 1000
export const REDEEM_MIN_INTERVAL_MS = 60 * 1000

/**
 * @param {object} deps
 * @param {(url: string) => Promise<Response>} deps.fetch
 * @param {(jwt: string) => { exp?: number }} deps.decodeJwt
 * @param {() => number} [deps.now]
 * @param {(msg: string, ...rest) => void} [deps.warn]
 * @param {number} [deps.minIntervalMs]
 * @param {number} [deps.renewMarginMs]
 * @param {number} [deps.clockSkewMs]
 */
export function createShortCodeRedeemer({
    fetch,
    decodeJwt,
    now = () => Date.now(),
    warn = () => {},
    minIntervalMs = REDEEM_MIN_INTERVAL_MS,
    renewMarginMs = LEAF_RENEW_MARGIN_MS,
    clockSkewMs = LEAF_CLOCK_SKEW_MS,
}) {
    // Keyed by (server, code): a swapped code neither joins the old code's
    // in-flight redemption nor is throttled out by its recent attempt.
    const attempts = new Map() // slot → epoch ms of last network attempt
    const inFlight = new Map() // slot → Promise<leaf|null>
    const slotOf = (serverKey, code) => `${serverKey}\n${code}`
    const slotServer = (slot) => slot.slice(0, slot.indexOf('\n'))

    function leafExpiryMs(jwt) {
        try {
            const { exp } = decodeJwt(jwt)
            return exp ? exp * 1000 : 0
        } catch (e) {
            return 0
        }
    }

    /**
     * Returns a presentable leaf, or null when the code is dead and nothing
     * usable is cached. A cached leaf counts only when `leafCode` says the
     * mount's current code minted it; a leaf of a retired code (or one
     * stored before the stamp existed) is dropped from the record and never
     * returned, not even as the fallback.
     */
    async function redeem({ credentials, credentialsStore, serverKey, serverUrl }) {
        const t = now()
        const code = credentials.token
        const slot = slotOf(serverKey, code)
        if (credentials.leafToken && credentials.leafCode !== code) {
            credentials.leafToken = null
            credentials.leafCode = null
        }
        const cached = credentials.leafToken || null
        const cachedExp = cached ? leafExpiryMs(cached) : 0
        const cachedUsable = cachedExp > t + clockSkewMs

        if (cachedExp > t + renewMarginMs) return cached

        const fallback = () => (cachedUsable ? cached : null)
        const stamp = (leaf) => {
            credentials.leafToken = leaf
            credentials.leafCode = code
        }
        // A joiner only ever joins a redemption of its own (server, code)
        // slot, so stamping the joiner's record with `code` cannot mislabel.
        const adopt = (leaf) => {
            if (leaf) stamp(leaf)
            return leaf || fallback()
        }

        const pending = inFlight.get(slot)
        if (pending) return adopt(await pending)

        const lastAttempt = attempts.get(slot) || 0
        if (t - lastAttempt < minIntervalMs) return fallback()
        attempts.set(slot, t)

        const attempt = (async () => {
            try {
                const url = new URL(
                    `/auth/chains/id/${code}`,
                    serverUrl || serverKey
                ).toString()
                const resp = await fetch(url)
                if (resp.ok) {
                    const leaf = (await resp.text()).trim()
                    if (leafExpiryMs(leaf) > t + clockSkewMs) {
                        stamp(leaf)
                        await credentialsStore.setItem(serverKey, credentials)
                        return leaf
                    }
                }
            } catch (e) {
                warn('Short-code redemption failed:', e?.message ?? e)
            }
            return null
        })()
        inFlight.set(slot, attempt)
        try {
            return adopt(await attempt)
        } finally {
            if (inFlight.get(slot) === attempt) inFlight.delete(slot)
        }
    }

    /**
     * Forget the throttle stamps for every code of this server — the 401
     * heal path re-redeems at once. Same signature as before the slots were
     * keyed by code, so sw.js is unchanged.
     */
    function reset(serverKey) {
        for (const slot of [...attempts.keys()]) {
            if (slotServer(slot) === serverKey) attempts.delete(slot)
        }
    }

    return { redeem, reset, leafExpiryMs }
}
